import React, { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SizeableText, Size, Typography } from 'fronton-react';
import { GAClickButton } from 'utils/analytics';
import { selectPageModelTitle } from 'store/pageModel';
import { CardWrapper, ImgWrapper, ContentWrapper, descriptionStyles } from './styles';

type InstoreCategoryCardProps = {
	name: string;
	description: string;
	categoryUrl: string;
	categoryImage: string;
	className?: string;
	type?: 'catalogue' | 'other';
};

export const InstoreCategoryCard = memo(
	({
		name,
		description,
		categoryUrl,
		categoryImage,
		className,
		type = 'catalogue',
	}: InstoreCategoryCardProps) => {
		const pageTitle = useSelector(selectPageModelTitle);

		const isCatalogueCard = useMemo(() => type === 'catalogue', [type]);
		const headingStyles = useMemo(
			() =>
				isCatalogueCard
					? { variant: 'h3' as SizeableText, size: undefined }
					: { variant: 'm' as SizeableText, size: 'body_long' as Size },
			[isCatalogueCard]
		);
		const descriptionTextStyles = useMemo(
			() =>
				isCatalogueCard
					? { variant: 'm' as SizeableText, size: 'body_long' as Size }
					: { variant: 'caption' as SizeableText, size: undefined },
			[isCatalogueCard]
		);

		const clickHandler = useCallback(() => {
			GAClickButton({
				elementBlock: 'servicePage',
				elementName: pageTitle,
				elementText: name,
			});
		}, [pageTitle, name]);

		return (
			<CardWrapper
				href={categoryUrl}
				className={className}
				data-qa="instore-category-card-wrapper"
				onClick={clickHandler}
			>
				<ImgWrapper isCatalogueCard={isCatalogueCard}>
					<img src={categoryImage} alt={name} data-qa="instore-category-card-img" />
				</ImgWrapper>
				<ContentWrapper isCatalogueCard={isCatalogueCard}>
					<Typography
						as="h4"
						color="text-primary"
						{...headingStyles}
						data-qa="instore-category-card-name"
					>
						{name}
					</Typography>
					<Typography
						className={descriptionStyles}
						color="text-secondary"
						{...descriptionTextStyles}
						data-qa="instore-category-card-description"
					>
						{description}
					</Typography>
				</ContentWrapper>
			</CardWrapper>
		);
	}
);
